import {
  CmpInvoiceKindEnum,
  CmpInvoiceStatusEnum,
  CmpMemberLifecycleStatusEnum,
} from "../../../graphql/types";

export type MemberInvoicePaymentRequestCapability = {
  invoiceId: string;
  available: boolean;
  isResend: boolean;
  reason?: string | null;
};

export type MemberInvoicePaymentRequestEvidence = {
  id: string;
  invoiceKind?: CmpInvoiceKindEnum | null;
  invoiceStatus: CmpInvoiceStatusEnum;
  amountDueInCents: number;
};

export type MemberAddOnPaymentRequestRow<
  Invoice extends MemberInvoicePaymentRequestEvidence,
> = {
  invoice: Invoice;
  actionAvailable: boolean;
  isResend: boolean;
  reason: string | null;
};

const unavailableReason =
  "We could not check if this add-on invoice can be emailed. Refresh the page and try again.";

const isReadOnlyLifecycle = (lifecycleStatus: CmpMemberLifecycleStatusEnum) =>
  lifecycleStatus === CmpMemberLifecycleStatusEnum.Past ||
  lifecycleStatus === CmpMemberLifecycleStatusEnum.Deleted;

/**
 * Resolves add-on rows independently from the primary membership invoice.
 * Missing or duplicate server capabilities fail closed, and only the exact
 * invoice capability can expose its Send/Resend action.
 */
export const getMemberUnpaidAddOnPaymentRequestRows = <
  Invoice extends MemberInvoicePaymentRequestEvidence,
>({
  capabilities,
  invoices,
  lifecycleStatus,
}: {
  capabilities: ReadonlyArray<MemberInvoicePaymentRequestCapability>;
  invoices: ReadonlyArray<Invoice>;
  lifecycleStatus: CmpMemberLifecycleStatusEnum;
}): Array<MemberAddOnPaymentRequestRow<Invoice>> => {
  const capabilitiesByInvoiceId = capabilities.reduce<
    Map<string, MemberInvoicePaymentRequestCapability | null>
  >((result, capability) => {
    const invoiceId = capability.invoiceId.trim();

    if (!invoiceId) {
      return result;
    }

    result.set(invoiceId, result.has(invoiceId) ? null : capability);
    return result;
  }, new Map());
  const readOnly = isReadOnlyLifecycle(lifecycleStatus);

  return invoices
    .filter(
      (invoice) =>
        invoice.invoiceKind === CmpInvoiceKindEnum.AddOn &&
        invoice.invoiceStatus === CmpInvoiceStatusEnum.Unpaid &&
        invoice.amountDueInCents > 0
    )
    .map((invoice) => {
      const capability = capabilitiesByInvoiceId.get(invoice.id);

      if (!capability) {
        return {
          invoice,
          actionAvailable: false,
          isResend: false,
          reason: unavailableReason,
        };
      }

      if (readOnly) {
        return {
          invoice,
          actionAvailable: false,
          isResend: capability.isResend,
          reason: "Past or Deleted members are read-only.",
        };
      }

      return {
        invoice,
        actionAvailable: capability.available,
        isResend: capability.isResend,
        reason: capability.available
          ? null
          : capability.reason?.trim() || unavailableReason,
      };
    });
};
